import { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import useSceneStore from '../../stores/sceneStore';
import { useRegionSelectStore } from '../../stores/regionSelectStore';
import { sceneCameraRef } from '../../utils/sceneCameraRef';
import { modelWorldBox, projectBoxToClient } from '../../utils/modelBox';

// ============================================================
// G2：区域框选遮罩层（DOM 级）——框选屏幕矩形 → 命中模型集合 → 区域批量 AI 操作
// 判定：模型世界包围盒 8 角投影到屏幕 AABB 与框选矩形求交（同一 client 坐标系）
// ============================================================

export default function RegionSelectOverlay() {
  const rootRef = useRef<HTMLDivElement>(null);
  const [rect, setRect] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const draggingRef = useRef(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        draggingRef.current = false;
        startRef.current = null;
        setRect(null);
        useSceneStore.getState().setRegionGenSelecting(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const relPos = (clientX: number, clientY: number) => {
    const r = rootRef.current?.getBoundingClientRect();
    return { x: clientX - (r?.left ?? 0), y: clientY - (r?.top ?? 0) };
  };

  /** 框选矩形（client 坐标）命中的模型 id 列表 */
  const pickModels = (r: { x1: number; y1: number; x2: number; y2: number }): string[] => {
    const cam = sceneCameraRef.get();
    const canvas = document.querySelector('canvas');
    if (!cam || !canvas) return [];
    const minX = Math.min(r.x1, r.x2);
    const maxX = Math.max(r.x1, r.x2);
    const minY = Math.min(r.y1, r.y2);
    const maxY = Math.max(r.y1, r.y2);
    const ids: string[] = [];
    const store = useSceneStore.getState();
    for (const m of store.models) {
      if (m.visible === false) continue;
      const box = modelWorldBox(m);
      if (!box) continue;
      const sb = projectBoxToClient(box, cam, canvas);
      if (!sb) continue;
      const overlap = sb.left <= maxX && sb.right >= minX && sb.top <= maxY && sb.bottom >= minY;
      if (overlap) ids.push(m.id);
    }
    return ids;
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    draggingRef.current = true;
    startRef.current = { x: e.clientX, y: e.clientY };
    const p = relPos(e.clientX, e.clientY);
    setRect({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current || !startRef.current) return;
    const s = relPos(startRef.current.x, startRef.current.y);
    const p = relPos(e.clientX, e.clientY);
    setRect({ x1: s.x, y1: s.y, x2: p.x, y2: p.y });
  };

  const finish = (e: React.PointerEvent) => {
    if (!draggingRef.current || !startRef.current) return;
    draggingRef.current = false;
    const s = startRef.current;
    startRef.current = null;
    setRect(null);
    useSceneStore.getState().setRegionGenSelecting(false);
    const dragRect = { x1: s.x, y1: s.y, x2: e.clientX, y2: e.clientY };
    if (Math.abs(dragRect.x2 - dragRect.x1) < 6 || Math.abs(dragRect.y2 - dragRect.y1) < 6) {
      message.warning('框选区域过小，请重新拖拽');
      return;
    }
    const ids = pickModels(dragRect);
    if (ids.length === 0) {
      message.warning('框选范围内没有模型，请调整范围后重试');
      return;
    }
    const rs = useRegionSelectStore.getState();
    rs.setSelection(ids);
    rs.requestOpen();
    message.success(`已框选 ${ids.length} 个模型（AI 助手已打开，可输入「统一为红色/隐藏选中」等）`);
  };

  const box = rect
    ? {
        left: Math.min(rect.x1, rect.x2),
        top: Math.min(rect.y1, rect.y2),
        width: Math.abs(rect.x2 - rect.x1),
        height: Math.abs(rect.y2 - rect.y1),
      }
    : null;

  return (
    <div
      ref={rootRef}
      data-testid="region-gen-overlay"
      style={{ position: 'absolute', inset: 0, zIndex: 316, cursor: 'crosshair', pointerEvents: 'auto', userSelect: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finish}
    >
      {box && (
        <div
          data-testid="region-gen-rect"
          style={{
            position: 'absolute',
            left: box.left,
            top: box.top,
            width: box.width,
            height: box.height,
            border: '2px dashed #1677ff',
            background: 'rgba(22,119,255,0.12)',
            boxSizing: 'border-box',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}
